// frontend/src/admin/components/AdminSidebar.js
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  CreditCard,
  Activity,
  Bot,
  Shield,
  BarChart3,
  AlertTriangle,
  DollarSign,
  MessageSquare,
  X
} from 'lucide-react';

const AdminSidebar = ({
  isOpen,
  onClose,
  currentPage,
  onPageChange,
  adminUser,
  liveStats, // optionnel : pour refléter l'état système
}) => {
  const location = useLocation();

  // ✅ menu aligné avec les vraies routes de AdminLayout
  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/admin', // index route
      description: "Vue d'ensemble générale",
    },
    {
      id: 'users',
      label: 'Utilisateurs',
      icon: Users,
      path: '/admin/users',
      description: 'Gestion des comptes',
      // badge: '1,234',
    },
    {
      id: 'payments',
      label: 'Paiements',
      icon: CreditCard,
      path: '/admin/payments',
      description: 'Stripe & facturation',
      // badge: '€2,456',
    },
    {
      id: 'ai-usage',
      label: 'Usage IA',
      icon: Bot,
      path: '/admin/ai-usage',
      description: 'Analytics OpenAI',
      badge: 'Hot',
    },
    {
      id: 'fiscal',
      label: 'Dashboard Fiscal',
      icon: DollarSign,
      path: '/admin/fiscal',
      description: 'Suivi fiscal AE',
      badge: 'New',
    },
    {
      id: 'system',
      label: 'Monitoring',
      icon: Activity,
      path: '/admin/system', // ⚠ route réelle dans AdminLayout
      description: 'Système & alertes',
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      path: '/admin/analytics',
      description: 'Métriques business',
    },
    {
      id: 'support',
      label: 'Support',
      icon: MessageSquare,
      path: '/admin/support',
      description: 'Tickets support',
      // badge: nb de tickets si tu veux plus tard
    },
  ];

  const isActive = (item) => {
    // Actif si l’URL commence par le path ou si currentPage matche l’id
    if (location.pathname === '/admin' && item.id === 'dashboard') return true;
    return (
      location.pathname.startsWith(item.path) ||
      currentPage === item.id
    );
  };

  const handleItemClick = (item) => {
    onPageChange?.(item.id);
    if (window.innerWidth < 768) {
      onClose?.();
    }
  };

  // ✅ petit bonus : refléter l'état système réel si liveStats arrive
  const systemStatus =
    liveStats?.systemHealth || 'healthy'; // healthy / warning / critical / unknown

  const systemLabel =
    systemStatus === 'healthy'
      ? 'Système OK'
      : systemStatus === 'warning'
      ? 'Attention'
      : systemStatus === 'critical'
      ? 'Critique'
      : 'Inconnu';

  return (
    <>
      <aside className={`admin-sidebar ${isOpen ? 'admin-sidebar--open' : ''}`}>
        {/* Header Sidebar */}
        <div className="admin-sidebar__header">
          <div className="admin-sidebar__logo">
            <div className="admin-logo-container">
              <Shield className="admin-logo-icon" />
              <div className="admin-logo-text">
                <span className="admin-logo-title">DragueMaster</span>
                <span className="admin-logo-subtitle">Admin Panel</span>
              </div>
            </div>
          </div>

          <button
            className="admin-sidebar__close"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar__nav">
          {/* Section Dashboard */}
          <div className="admin-nav-section">
            <div className="admin-nav-section__title">
              <BarChart3 size={16} />
              Tableau de Bord
            </div>

            {menuItems.slice(0, 1).map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`admin-nav-item ${
                    isActive(item) ? 'admin-nav-item--active' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="admin-nav-item__icon">
                    <Icon size={20} />
                  </div>
                  <div className="admin-nav-item__content">
                    <span className="admin-nav-item__label">{item.label}</span>
                    <span className="admin-nav-item__description">
                      {item.description}
                    </span>
                  </div>
                  {item.badge && (
                    <div className="admin-nav-item__badge">{item.badge}</div>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Section Gestion */}
          <div className="admin-nav-section">
            <div className="admin-nav-section__title">
              <Users size={16} />
              Gestion
            </div>

            {menuItems.slice(1, 5).map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`admin-nav-item ${
                    isActive(item) ? 'admin-nav-item--active' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="admin-nav-item__icon">
                    <Icon size={20} />
                  </div>
                  <div className="admin-nav-item__content">
                    <span className="admin-nav-item__label">{item.label}</span>
                    <span className="admin-nav-item__description">
                      {item.description}
                    </span>
                  </div>
                  {item.badge && (
                    <div
                      className={`admin-nav-item__badge ${
                        item.badge === 'Hot' ? 'admin-nav-item__badge--hot' : ''
                      }`}
                    >
                      {item.badge}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Section Système / Support */}
          <div className="admin-nav-section">
            <div className="admin-nav-section__title">
              <Activity size={16} />
              Système
            </div>

            {menuItems.slice(5).map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`admin-nav-item ${
                    isActive(item) ? 'admin-nav-item--active' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="admin-nav-item__icon">
                    <Icon size={20} />
                  </div>
                  <div className="admin-nav-item__content">
                    <span className="admin-nav-item__label">{item.label}</span>
                    <span className="admin-nav-item__description">
                      {item.description}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer avec infos admin */}
        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__user-info">
            <div className="admin-user-avatar">
              <Shield size={20} />
            </div>
            <div className="admin-user-details">
              <div className="admin-user-name">
                {adminUser?.username || 'Admin'}
              </div>
              <div className="admin-user-role">
                {adminUser?.role === 'super_admin'
                  ? '👑 Super Admin'
                  : '🛡️ Admin'}
              </div>
            </div>
          </div>

          <div className="admin-sidebar__stats">
            <div className="admin-quick-stat">
              <AlertTriangle size={14} />
              <span>0 Alertes</span>
            </div>
            <div className="admin-quick-stat">
              <Activity size={14} />
              <span>{systemLabel}</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
